'use strict';

import { listenDOMEvent } from '@jspartials/dom-api-utils';
import {
  addClassToElement,
  removeClassToElement,
  hasClassElement,
  classes
} from '@jspartials/class-list-utils';
import {
  setHtmlUnscrollable,
  setHtmlScrollable
} from '@jspartials/general-utils';
import {
  COOKIE_DURATION_ONE_YEAR,
  COOKIE_NAME_FOR_PREFERRED_LOCALE,
  setCookie
} from '@jspartials/cookie-utils';

// Classes
const SITE_SELECTOR_TRIGGER_OPEN_CLASSNAME = 'js-site-selector__trigger-open';
const MAIN_SITE_SELECTOR_CLASSNAME = 'js-site-selector';
const SITE_SELECTOR_CONTENT_CLASSNAME = 'js-site-selector__content';
const SITE_SELECTOR_CLASSNAME = 'js-site-selector__dropdown';
const SITE_SELECTOR_TITLE_TEXT_CLASSNAME = 'js-site-selector__title-text';
const SITE_SELECTOR_DESCRIPTION_TEXT_CLASSNAME =
  'js-site-selector__description-text';
const SITE_SELECTOR_SELECTED_OPTION_CLASSNAME =
  'js-site-selector__selected-option';
const SITE_SELECTOR_SELECTED_OPTION_IMAGE_CLASSNAME =
  'js-site-selector__selected-option-image';
const SITE_SELECTOR_SELECTED_OPTION_TEXT_CLASSNAME =
  'js-site-selector__selected-option-text';
const SITE_SELECTOR_OPTION_CLASSNAME = 'js-site-selector__dropdown-option';
const SITE_SELECTOR_OPTION_COUNTRY_TEXT_CLASSNAME =
  'js-site-selector__dropdown-option-country-text';
const SITE_SELECTOR_ARROW_CLASSNAME = 'js-site-selector__selected-option-arrow';
const SITE_SELECTOR_REDIRECT_LINK_TEXT_CLASSNAME =
  'js-site-selector__redirect-link-text';
const SITE_SELECTOR_ICON_CLOSE_CLASSNAME = 'js-site-selector_icon-close';

// Selectors
const MAIN_SITE_SELECTOR_SELECTOR = `.${MAIN_SITE_SELECTOR_CLASSNAME}`;
const SITE_SELECTOR_CONTENT_SELECTOR = `.${SITE_SELECTOR_CONTENT_CLASSNAME}`;
const SITE_SELECTOR_TITLE_TEXT_SELECTOR = `.${SITE_SELECTOR_TITLE_TEXT_CLASSNAME}`;
const SITE_SELECTOR_DESCRIPTION_TEXT_SELECTOR = `.${SITE_SELECTOR_DESCRIPTION_TEXT_CLASSNAME}`;
const SITE_SELECTOR_SELECTOR = `.${SITE_SELECTOR_CLASSNAME}`;
const SITE_SELECTOR_SELECTED_OPTION_SELECTOR = `.${SITE_SELECTOR_SELECTED_OPTION_CLASSNAME}`;
const SITE_SELECTOR_SELECTED_OPTION_IMAGE_SELECTOR = `.${SITE_SELECTOR_SELECTED_OPTION_IMAGE_CLASSNAME}`;
const SITE_SELECTOR_SELECTED_OPTION_TEXT_SELECTOR = `.${SITE_SELECTOR_SELECTED_OPTION_TEXT_CLASSNAME}`;
const SITE_SELECTOR_OPTION_SELECTOR = `.${SITE_SELECTOR_OPTION_CLASSNAME}`;
const SITE_SELECTOR_OPTION_COUNTRY_TEXT_SELECTOR = `.${SITE_SELECTOR_OPTION_COUNTRY_TEXT_CLASSNAME}`;
const SITE_SELECTOR_TRIGGER_OPEN_SELECTOR = `.${SITE_SELECTOR_TRIGGER_OPEN_CLASSNAME}`;
const SITE_SELECTOR_ARROW_SELECTOR = `.${SITE_SELECTOR_ARROW_CLASSNAME}`;
const SITE_SELECTOR_REDIRECT_LINK_TEXT_SELECTOR = `.${SITE_SELECTOR_REDIRECT_LINK_TEXT_CLASSNAME}`;
const SITE_SELECTOR_ICON_CLOSE_SELECTOR = `.${SITE_SELECTOR_ICON_CLOSE_CLASSNAME}`;

// Elements
const mainSiteSelector = document.querySelector(MAIN_SITE_SELECTOR_SELECTOR);
const siteSelectorContent = document.querySelector(
  SITE_SELECTOR_CONTENT_SELECTOR
);
const titleText = document.querySelectorAll(SITE_SELECTOR_TITLE_TEXT_SELECTOR);
const descriptionText = document.querySelector(
  SITE_SELECTOR_DESCRIPTION_TEXT_SELECTOR
);
const siteSelector = document.querySelector(SITE_SELECTOR_SELECTOR);
const selectedOption = document.querySelector(
  SITE_SELECTOR_SELECTED_OPTION_SELECTOR
);
const selectedOptionImage = document.querySelector(
  SITE_SELECTOR_SELECTED_OPTION_IMAGE_SELECTOR
);
const selectedOptionText = document.querySelector(
  SITE_SELECTOR_SELECTED_OPTION_TEXT_SELECTOR
);
const arrowSelector = document.querySelector(SITE_SELECTOR_ARROW_SELECTOR);
const redirectLinkText = document.querySelector(
  SITE_SELECTOR_REDIRECT_LINK_TEXT_SELECTOR
);
const iconClose = document.querySelector(SITE_SELECTOR_ICON_CLOSE_SELECTOR);

// Variables
let isOpened = false;

// Functions

//Close the list of countries and set the classes depending on the variable
const closeSelector = () => {
  isOpened = false;
  removeClassToElement(siteSelector, classes.is.open);
  removeClassToElement(arrowSelector, classes.is.open);
  removeClassToElement(selectedOption, classes.is.open);
};

//Opens the list of countries and set the classes depending on the variable
const openSelector = () => {
  if (!isOpened) {
    isOpened = true;
    addClassToElement(siteSelector, classes.is.open);
    addClassToElement(arrowSelector, classes.is.open);
    addClassToElement(selectedOption, classes.is.open);
  } else {
    closeSelector();
  }
};

const closeModal = () => {
  if (!hasClassElement(mainSiteSelector, classes.hidden)) {
    addClassToElement(mainSiteSelector, classes.hidden);
    setHtmlScrollable();
    updateLocaleCookie(selectedOption.dataset.current);
  }
};

//Changes the main/selected button content and its properties
const changeSelectedOption = (
  selectedOptionTextContent, // Australia
  selectedLocale, // en
  redirectLinkUrl,
  titleTextContent, // Select a country...
  descriptionTextContent, //  Indicate in which country...
  redirectLinkTextContent, //  GO
  selectedOptionImageContent
) => {
  descriptionText.innerHTML = descriptionTextContent;
  titleText.forEach((text) => (text.innerText = titleTextContent));
  selectedOption.dataset.locale = selectedLocale;
  selectedOptionImage.src = selectedOptionImageContent;
  selectedOptionText.textContent = selectedOptionTextContent;
  redirectLinkText.innerHTML = redirectLinkTextContent;
  redirectLinkText.href = redirectLinkUrl;
};

/**
 * Update site locale
 */
const updateSite = () => {
  const locale = selectedOption.dataset.locale;

  updateLocaleCookie(locale);
};

/**
 * Update locale cookie and and site url
 */
const updateLocaleCookie = (locale) => {
  setCookie(COOKIE_NAME_FOR_PREFERRED_LOCALE, locale, COOKIE_DURATION_ONE_YEAR, true);
};

// Inits the onclick event of every trigger open selector
const initSiteSelector = () => {
  const siteSelectorTriggersOpen = document.querySelectorAll(
    SITE_SELECTOR_TRIGGER_OPEN_SELECTOR
  );

  if (siteSelectorTriggersOpen) {
    siteSelectorTriggersOpen.forEach((siteSelectorTriggersOpen) => {
      listenDOMEvent(siteSelectorTriggersOpen, 'click', openSelector);
    });
  }
};

// Inits the onclick event of every button
const initOptions = () => {
  const siteSelectorOptions = document.querySelectorAll(
    SITE_SELECTOR_OPTION_SELECTOR
  );

  if (siteSelectorOptions) {
    siteSelectorOptions.forEach((siteSelectorOption) => {
      listenDOMEvent(siteSelectorOption, 'click', function () {
        const selectedLocale = this.dataset.locale;

        // Change the country text depending on the language
        changeCountriesTextLanguage(selectedLocale, siteSelectorOptions);

        const redirectLinkUrl = this.value;
        const titleText = this.dataset.title;
        const descriptionText = this.dataset.msg;
        const redirectLinkText = this.dataset.btn;
        const selectedOptionImage = this.dataset.img;
        const selectedOptionText = this.innerText;

        changeSelectedOption(
          selectedOptionText,
          selectedLocale,
          redirectLinkUrl,
          titleText,
          descriptionText,
          redirectLinkText,
          selectedOptionImage
        );
      });
    });
  }
};

const changeCountriesTextLanguage = (selectedLocale, siteSelectorOptions) => {
  siteSelectorOptions.forEach((button) => {
    const optionLanguageText = 'button.dataset.' + selectedLocale.split('_')[0];

    button.querySelector(SITE_SELECTOR_OPTION_COUNTRY_TEXT_SELECTOR).innerText =
      eval(optionLanguageText);
  });
};

// Init
document.addEventListener('DOMContentLoaded', function () {
  if (mainSiteSelector) {
    setHtmlUnscrollable();
    initSiteSelector();
    initOptions();

    listenDOMEvent(redirectLinkText, 'click', function () {
      updateSite();
    });

    listenDOMEvent(iconClose, 'click', function () {
      closeModal();
    });

    // Update languages and update site
    if (inputsCountry.length > 0) {
      inputsCountry.forEach((inputCountry) => {
        listenDOMEvent(inputCountry, 'change', function (event) {
          updateLanguages(event.target);
        });
      });
    }

    // Close the site-selector when clicking outside
    mainSiteSelector.addEventListener('click', function (event) {
      if (event.target === mainSiteSelector) {
        closeModal();
      }
    });

    // Close the site-selector when clicking outside
    listenDOMEvent(window, 'click', function (event) {
      if (
        !event.target.closest('.js-site-selector__dropdown') &&
        !event.target.closest('.js-site-selector__selected-option')
      ) {
        closeSelector();
      }
    });
  }
});
